import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as CrossIcon } from "../../assets/icons/cross.svg";
import useReaderStore from "../../context/readerStore";
import useActionGuard from "../../hooks/useActionGuard";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { deleteBookmarkById } from "../../services/bookmarkService";
import "./Bookmarks.css";

const Bookmarks = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const {
    setCurrentPage,
    document,
    documentType,
    toggleBookmarksSidebar,
    annotations,
    setAnnotationData,
    setAnnotationLoading,
    setAnnotationError,
  } = useReaderStore();

  // Memoize apiBookmarks sorted by page/section number (document order)
  const apiBookmarks = useMemo(() => {
    const bookmarks = annotations?.bookmarks?.data || [];
    return [...bookmarks].sort(
      (a, b) => Number(a.page_number || 0) - Number(b.page_number || 0),
    );
  }, [annotations?.bookmarks?.data]);
  const isLoadingBookmarks = annotations?.bookmarks?.isLoading || false;
  const bookmarksError = annotations?.bookmarks?.error || null;

  const executeBookmarkDelete = useCallback(
    async (bm) => {
      setAnnotationLoading("bookmarks", true);

      await deleteBookmarkById({
        bookmarkId: bm.id,
        currentBookmarks: apiBookmarks,
        onSuccess: (updatedBookmarks) => {
          setAnnotationData("bookmarks", updatedBookmarks);
          setAnnotationLoading("bookmarks", false);
        },
        onError: (error) => {
          console.error("Error deleting bookmark:", error);
          setAnnotationError(
            "bookmarks",
            error?.message || t("bookmarks.deleteError"),
          );
          setAnnotationLoading("bookmarks", false);
        },
      });
    },
    [
      apiBookmarks,
      setAnnotationData,
      setAnnotationLoading,
      setAnnotationError,
      t,
    ],
  );

  const { run: handleBookmarkDelete, isPending: isBookmarkDeletePending } =
    useActionGuard(executeBookmarkDelete, {
      keyBy: (bm) => bm?.id,
    });

  const navigateToBookmarkPage = useCallback(
    (pageNumber) => {
      const targetPage = Number(pageNumber || 0);
      if (!targetPage) return;

      if (documentType === "pdf" && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("reader:go-to-page", {
            detail: { page: targetPage },
          }),
        );
        setCurrentPage(targetPage);
        if (isMobile) toggleBookmarksSidebar();
        return;
      }

      setCurrentPage(targetPage);
      if (isMobile) toggleBookmarksSidebar();
    },
    [documentType, setCurrentPage, isMobile, toggleBookmarksSidebar],
  );

  return (
    <div
      className="bookmarks open"
      role="complementary"
      aria-label={t("bookmarks.title")}
    >
      <div className="bookmarks-header">
        <h3 tabIndex="-1">{t("bookmarks.title")}</h3>
        {isMobile && (
          <button
            className="sidebar-close-button"
            onClick={toggleBookmarksSidebar}
            aria-label={t("bookmarks.close")}
          >
            <CloseIcon width={24} height={24} />
          </button>
        )}
      </div>

      <div className="bookmarks-content">
        {isLoadingBookmarks ? (
          <div className="bookmarks-loading" role="status" aria-live="polite">
            <div className="bookmarks-spinner"></div>
            <p>{t("bookmarks.loading")}</p>
          </div>
        ) : bookmarksError ? (
          <div className="bookmarks-error" role="alert">
            <p>{t("bookmarks.error", { error: bookmarksError })}</p>
          </div>
        ) : apiBookmarks.length > 0 ? (
          <div className="bookmarks-container">
            {apiBookmarks.map((bm) => (
              <div key={bm.id} className="bookmark-item-wrapper">
                <button
                  className="bookmark-item"
                  onClick={() => {
                    navigateToBookmarkPage(bm.page_number);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigateToBookmarkPage(bm.page_number);
                    }
                  }}
                  title={t("bookmarks.navigate")}
                  type="button"
                  aria-label={t("bookmarks.navigateLabel", {
                    label:
                      bm.title ||
                      t("notes.pageTitle", { page: bm.page_number }),
                  })}
                >
                  <div
                    className="bookmark-title"
                    title={
                      bm.title || t("notes.pageTitle", { page: bm.page_number })
                    }
                  >
                    {bm.title || t("notes.pageTitle", { page: bm.page_number })}
                  </div>
                </button>
                <button
                  className="bookmark-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBookmarkDelete(bm);
                  }}
                  disabled={isBookmarkDeletePending(bm.id)}
                  title={t("bookmarks.remove")}
                  type="button"
                  aria-label={t("bookmarks.removeLabel", {
                    label:
                      bm.title ||
                      t("notes.pageTitle", { page: bm.page_number }),
                  })}
                >
                  <CrossIcon width={14} height={14} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="bookmarks-empty">
            <p>{t("bookmarks.noItems")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bookmarks;
